import React from "react";
import { Users, Activity } from "lucide-react";

/**
 * Posted-vs-Not-Posted bifurcation block used on the Reports page AND on the
 * Quick Scan (Dashboard) page. Stories do NOT count as "posted" per user's
 * explicit spec — a creator with only stories is still "Not Posted".
 *
 * ER tiles use the user's PER-CREATOR MEAN formulas (2026-Feb directive,
 * "Overall is intersection, not union"). No caps.
 *   ER Reels   = Σ per-creator ER Reels ÷ N creators who posted Reels
 *   ER Static  = Σ per-creator ER Static ÷ N creators who posted Static
 *   ER Overall = Σ (ER Reels + ER Static) for creators who posted BOTH
 *                ÷ N creators who posted BOTH
 *
 * Per-creator ERs come from build_summary (server-side, formula-locked):
 *   engagement_rate_reels  = (avg_reel_likes+comments) / avg_reel_views × 100
 *   engagement_rate_static = (avg_static_likes+comments) / followers    × 100
 *
 * Props:
 *   summary - array of per-creator objects (has matched_posts, stories,
 *             followers, engagement_rate_reels, engagement_rate_static)
 *   posts   - array of raw post rows for the same window (unused here now,
 *             kept for backwards compat; strip pulls from summary only).
 *   testIdPrefix - optional prefix for data-testids (default: "bif")
 *   visible - object of card-key → boolean; when a key is false its tile is
 *             hidden. Unlisted keys default to visible.
 */
export const PostedBifurcation = ({ summary = [], posts = [], testIdPrefix = "bif", visible = {}, erOverallMode = "sum" }) => {
  if (!summary || summary.length === 0) return null;
  const isOn = (k) => visible[k] !== false;
  const total = summary.length;

  // 5-way split — matches user's representation spec:
  //   Total posted           → matched_posts > 0
  //   Posted WITH stories    → matched_posts > 0 AND stories > 0
  //   Posted ONLY (no story) → matched_posts > 0 AND stories == 0
  //   Stories-only           → matched_posts == 0 AND stories > 0
  //   Nothing at all         → matched_posts == 0 AND stories == 0
  const posted = summary.filter((s) => (s.matched_posts || 0) > 0).length;
  const postedWithStories = summary.filter(
    (s) => (s.matched_posts || 0) > 0 && (s.stories || 0) > 0
  ).length;
  const postedOnly = summary.filter(
    (s) => (s.matched_posts || 0) > 0 && (s.stories || 0) === 0
  ).length;
  const storiesOnly = summary.filter(
    (s) => (s.matched_posts || 0) === 0 && (s.stories || 0) > 0
  ).length;
  const nothing = summary.filter(
    (s) => (s.matched_posts || 0) === 0 && (s.stories || 0) === 0
  ).length;

  const frac = (n) => `${n} / ${total}`;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  // ── Per-creator MEAN ER formulas (user's 2026-Feb directive) ──
  //   ER Reels   = Σ per-creator ER Reels ÷ N creators who posted Reels
  //   ER Static  = Σ per-creator ER Static ÷ N creators who posted Static/Carousels
  //   ER Overall = Σ (ER Reels + ER Static) for creators who posted BOTH
  //                ÷ N creators who posted BOTH (intersection, NOT union)
  // N is derived from actual post-count fields (video_posts_count /
  // static_posts_count), NOT from "creators with a populated ER". Creators
  // whose ER wasn't computed (e.g. followers unknown after collab attribution)
  // contribute 0 to the sum but are still counted — matches "N creators who
  // POSTED", which is what the user wants displayed.
  const reelsPosters = summary.filter((s) => Number(s.video_posts_count || 0) > 0);
  const staticPosters = summary.filter((s) => Number(s.static_posts_count || 0) > 0);
  const bothPosters = summary.filter(
    (s) => Number(s.video_posts_count || 0) > 0
        && Number(s.static_posts_count || 0) > 0
  );
  const reelsSum = reelsPosters.reduce(
    (a, s) => a + Number(s.engagement_rate_reels || 0), 0);
  const staticSum = staticPosters.reduce(
    (a, s) => a + Number(s.engagement_rate_static || 0), 0);
  const bothSum = bothPosters.reduce(
    (a, s) => a + Number(s.engagement_rate_reels || 0)
                + Number(s.engagement_rate_static || 0), 0);
  const erReels = reelsPosters.length ? reelsSum / reelsPosters.length : null;
  const erStatic = staticPosters.length ? staticSum / staticPosters.length : null;
  const nReelsPosters = reelsPosters.length;
  const nStaticPosters = staticPosters.length;
  const nBothPosters = bothPosters.length;
  const postedCreators = summary.filter((s) => (s.matched_posts || 0) > 0);
  const nPosters = postedCreators.length;
  // Default: sum-of-two ÷ N. "mean" mode (Nikon-only per user directive):
  // mean-of-two ÷ N — halves the double-count inflation.
  const erOverall = bothPosters.length
    ? bothSum / (erOverallMode === "mean" ? 2 : 1) / bothPosters.length
    : null;
  const fmt = (v) => (v === null ? "N/A" : `${v.toFixed(2)}%`);

  const anyBifTile = ["posted","posted-stories","posted-only","stories-only","nothing"].some(isOn);
  const anyErTile = ["er-strip-reels","er-strip-static","er-strip-overall"].some(isOn);
  const showErTable = isOn("er-detail-table");
  if (!anyBifTile && !anyErTile && !showErTable) return null;

  return (
    <div
      className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_12px_-2px_rgba(15,23,42,0.04)]"
      data-testid={`${testIdPrefix}-bifurcation`}
    >
      {anyBifTile && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
            <Users size={10} className="text-emerald-600" /> Posted vs Not-Posted
            <span className="text-slate-300 normal-case tracking-normal">
              — stories don't count as posted
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mb-5">
            {isOn("posted") && <Tile
              label="✓ Total Posted"
              value={<>{frac(posted)}<span className="ml-1 text-[10px] font-mono opacity-70">({pct(posted)}%)</span></>}
              testId={`${testIdPrefix}-posted`}
              tone="emerald"
            />}
            {isOn("posted-stories") && <Tile
              label="Posted + Stories"
              value={<>{frac(postedWithStories)}<span className="ml-1 text-[10px] font-mono opacity-70">({pct(postedWithStories)}%)</span></>}
              testId={`${testIdPrefix}-posted-stories`}
              tone="fuchsia"
              hint="Creators who posted content AND ALSO have stories"
            />}
            {isOn("posted-only") && <Tile
              label="Posted only (no stories)"
              value={<>{frac(postedOnly)}<span className="ml-1 text-[10px] font-mono opacity-70">({pct(postedOnly)}%)</span></>}
              testId={`${testIdPrefix}-posted-only`}
              tone="violet"
              hint="Creators who posted content but have NO stories"
            />}
            {isOn("stories-only") && <Tile
              label="Stories only (no posts)"
              value={<>{frac(storiesOnly)}<span className="ml-1 text-[10px] font-mono opacity-70">({pct(storiesOnly)}%)</span></>}
              testId={`${testIdPrefix}-stories-only`}
              tone="amber"
              hint="Creators who posted ONLY stories, no Reels/Carousel/Static"
            />}
            {isOn("nothing") && <Tile
              label="✗ Nothing at all"
              value={<>{frac(nothing)}<span className="ml-1 text-[10px] font-mono opacity-70">({pct(nothing)}%)</span></>}
              testId={`${testIdPrefix}-nothing`}
              tone="red"
              hint="Creators who posted nothing and have no stories either"
            />}
          </div>
        </>
      )}

      {anyErTile && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
            <Activity size={10} className="text-pink-600" /> Engagement rate · mean per creator
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mb-4">
            {isOn("er-strip-reels") && <ErTile
              label="ER Reels"
              avg={fmt(erReels)}
              caption={`${nReelsPosters} creator${nReelsPosters === 1 ? "" : "s"} posted Reels/Videos`}
              tone="pink"
              testId={`${testIdPrefix}-er-reels`}
              hint="Σ per-creator ER Reels ÷ N creators who posted Reels"
            />}
            {isOn("er-strip-static") && <ErTile
              label="ER Static"
              avg={fmt(erStatic)}
              caption={`${nStaticPosters} creator${nStaticPosters === 1 ? "" : "s"} posted Static/Carousels`}
              tone="emerald"
              testId={`${testIdPrefix}-er-static`}
              hint="Σ per-creator ER Static ÷ N creators who posted Static/Carousels"
            />}
            {isOn("er-strip-overall") && <ErTile
              label="ER Overall"
              avg={fmt(erOverall)}
              caption={`${nBothPosters} creator${nBothPosters === 1 ? "" : "s"} posted BOTH Reels & Static`}
              tone="violet"
              testId={`${testIdPrefix}-er-overall`}
              hint={erOverallMode === "mean"
                ? "mean of (ER Reels + ER Static)/2 for creators who posted BOTH ÷ N posted both"
                : "Σ (ER Reels + ER Static) for creators who posted BOTH ÷ N creators who posted both"}
            />}
          </div>
        </>
      )}

      {showErTable && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 overflow-hidden text-xs">
          <table className="w-full">
            <thead className="text-[10px] font-mono uppercase text-slate-400 bg-white">
              <tr>
                <th className="text-left px-3 py-1.5">Metric</th>
                <th className="text-right px-3 py-1.5">Creators</th>
                <th className="text-right px-3 py-1.5">Σ Creator ERs</th>
                <th className="text-right px-3 py-1.5">Method</th>
                <th className="text-right px-3 py-1.5">ER %</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-slate-200" data-testid={`${testIdPrefix}-agg-reels`}>
                <td className="px-3 py-1.5 text-pink-600">ER Reels</td>
                <td className="text-right px-3 py-1.5 text-slate-700">{nReelsPosters}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">{reelsSum.toFixed(2)}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">Σ ÷ N posted Reels</td>
                <td className="text-right px-3 py-1.5 text-slate-900 font-semibold">{fmt(erReels)}</td>
              </tr>
              <tr className="border-t border-slate-200" data-testid={`${testIdPrefix}-agg-static`}>
                <td className="px-3 py-1.5 text-emerald-600">ER Static</td>
                <td className="text-right px-3 py-1.5 text-slate-700">{nStaticPosters}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">{staticSum.toFixed(2)}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">Σ ÷ N posted Static</td>
                <td className="text-right px-3 py-1.5 text-slate-900 font-semibold">{fmt(erStatic)}</td>
              </tr>
              <tr className="border-t border-slate-200" data-testid={`${testIdPrefix}-agg-combined`}>
                <td className="px-3 py-1.5 text-violet-600">ER Overall (posted BOTH)</td>
                <td className="text-right px-3 py-1.5 text-slate-700">{nBothPosters}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">{bothSum.toFixed(2)}</td>
                <td className="text-right px-3 py-1.5 text-slate-500">Σ (R+S) ÷ N posted both</td>
                <td className="text-right px-3 py-1.5 text-slate-900 font-semibold">{fmt(erOverall)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const toneMap = {
  gray: ["bg-slate-50", "border-slate-200", "text-slate-400", "text-slate-900"],
  emerald: ["bg-emerald-50", "border-emerald-200", "text-emerald-600", "text-emerald-700"],
  fuchsia: ["bg-fuchsia-50", "border-fuchsia-200", "text-fuchsia-600", "text-fuchsia-700"],
  red: ["bg-red-50", "border-red-200", "text-red-600", "text-red-700"],
  amber: ["bg-amber-50", "border-amber-200", "text-amber-600", "text-amber-700"],
  pink: ["bg-pink-50", "border-pink-200", "text-pink-600", "text-pink-700"],
  violet: ["bg-violet-50", "border-violet-200", "text-violet-600", "text-violet-700"],
};

const Tile = ({ label, value, testId, tone, hint }) => {
  const [bg, border, labelC, valueC] = toneMap[tone] || toneMap.gray;
  return (
    <div className={`rounded-xl ${bg} border ${border} px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.08)] transition-all duration-200 ease-out`}>
      <div className={`text-[10px] font-mono uppercase tracking-wider ${labelC}`} title={hint || undefined}>
        {label}
      </div>
      <div className={`text-base font-semibold ${valueC}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
};

const ErTile = ({ label, avg, caption, tone, testId, hint }) => {
  const [bg, border, labelC, valueC] = toneMap[tone] || toneMap.gray;
  return (
    <div className={`rounded-xl ${bg} border ${border} px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.08)] transition-all duration-200 ease-out`} title={hint || undefined}>
      <div className={`text-[10px] font-mono uppercase tracking-wider ${labelC}`}>
        {label}
      </div>
      <div className={`text-xl font-bold tracking-tight ${valueC} mt-0.5`} data-testid={testId}>
        {avg}
      </div>
      <div className="text-[10px] font-mono text-slate-400 mt-0.5">{caption}</div>
    </div>
  );
};

const ErRow = ({ label, stats, tone, testId }) => {
  const [, , labelC] = toneMap[tone] || toneMap.gray;
  return (
    <tr className="border-t border-slate-200" data-testid={testId}>
      <td className={`px-3 py-1.5 ${labelC}`}>{label}</td>
      <td className="text-right px-3 py-1.5 text-slate-700">{stats.n}</td>
      <td className="text-right px-3 py-1.5 text-slate-500">{stats.sum}%</td>
      <td className="text-right px-3 py-1.5 text-slate-900 font-semibold">{stats.avg}%</td>
    </tr>
  );
};

/**
 * Returns the 4-way category for a single creator row so the summary tables
 * can render a colored status badge in the last column. Same categories as
 * the tiles above (except Total Posted is implicit).
 */
export const creatorStatus = (s) => {
  const posted = (s.matched_posts || 0) > 0;
  const stories = (s.stories || 0) > 0;
  if (posted && stories) return { key: "posted+stories", label: "Posted + Stories", tone: "fuchsia" };
  if (posted) return { key: "posted", label: "Posted", tone: "emerald" };
  if (stories) return { key: "stories-only", label: "Stories only", tone: "amber" };
  return { key: "nothing", label: "Nothing", tone: "red" };
};

const badgeToneClass = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  red: "bg-red-50 border-red-200 text-red-700",
};

/**
 * Colored pill for a creator's Posted/Not-Posted status. Reused in Reports.jsx
 * and Dashboard.jsx summary tables — same colors as the bifurcation tiles.
 */
export const CreatorStatusBadge = ({ creator, testId }) => {
  const st = creatorStatus(creator);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${badgeToneClass[st.tone]}`}
      data-testid={testId || `status-badge-${st.key}`}
      title={st.label}
    >
      ● {st.label}
    </span>
  );
};
